(() => {
  'use strict';
  const modes = new Set(['hikoki', 'metabo', 'both']);
  const root = () => window.tcd?.routes?.root || '/';
  const route = name => `${root()}cart${name ? `/${name}` : ''}.js`;
  const props = item => item.properties || {};
  const brand = item => props(item)._tcd_kit_brand;
  const role = item => props(item)._tcd_kit_role;
  const platform = item => props(item)._tcd_kit_platform;
  let queue = Promise.resolve();
  let checkoutBusy = false;

  function groups(cart) {
    const result = new Map();
    for (const item of cart.items || []) {
      const id = props(item)._tcd_kit_id;
      if (!id) continue;
      if (!result.has(id)) result.set(id, []);
      result.get(id).push(item);
    }
    return result;
  }

  function requiredLevel(items) {
    const kitModes = new Set(items.map(item => props(item)._tcd_kit_mode));
    if (kitModes.size !== 1) return 0;
    const mode = [...kitModes][0];
    if (!modes.has(mode)) return 0;
    const requiredBrands = mode === 'both' ? ['hikoki', 'metabo'] : [mode];
    if (items.some(item => !requiredBrands.includes(brand(item)) || !['tool', 'set'].includes(role(item)) || !Number.isInteger(item.quantity) || item.quantity < 1)) return 0;
    const tools = items.filter(item => role(item) === 'tool');
    const sets = items.filter(item => role(item) === 'set');
    if (sets.length !== requiredBrands.length || sets.some(item => item.quantity !== 1)) return 0;
    for (const currentBrand of requiredBrands) {
      const brandTools = tools.filter(item => brand(item) === currentBrand);
      const power = sets.find(item => brand(item) === currentBrand);
      if (!brandTools.length || !power) return 0;
      if (currentBrand === 'metabo') {
        if (platform(power) !== '18v' || brandTools.some(item => platform(item) !== '18v')) return 0;
      } else {
        if (!['18v', 'multivolt'].includes(platform(power))) return 0;
        if (brandTools.some(item => !['18v', '36v'].includes(platform(item)))) return 0;
        if (brandTools.some(item => platform(item) === '36v') && platform(power) !== 'multivolt') return 0;
      }
    }
    const quantity = tools.reduce((total, item) => total + item.quantity, 0);
    return quantity >= 4 ? 10 : quantity >= 2 ? 5 : 0;
  }

  function propertiesFor(item, level) {
    const result = { ...props(item) };
    delete result['Kit saving'];
    delete result._tcd_kit_discount_level;
    delete result._tcd_kit_incomplete;
    if (level) {
      result['Kit saving'] = `${level}%`;
      result._tcd_kit_discount_level = String(level);
    } else {
      result._tcd_kit_incomplete = 'true';
    }
    return result;
  }

  function needsUpdate(item, level) {
    const p = props(item);
    return level
      ? p._tcd_kit_discount_level !== String(level) || p['Kit saving'] !== `${level}%` || Boolean(p._tcd_kit_incomplete)
      : Boolean(p._tcd_kit_discount_level || p['Kit saving']) || p._tcd_kit_incomplete !== 'true';
  }

  async function readCart() {
    const response = await fetch(`${route('')}?_tcd_kit_check=${Date.now()}`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to check your kit saving. Please refresh your basket and try again.');
    return response.json();
  }

  async function reconcile(seed) {
    let latest = seed && typeof seed.item_count === 'number' && Array.isArray(seed.items) ? seed : await readCart();
    let changed = false;
    for (let attempt = 0; attempt < 100; attempt += 1) {
      let pending = null;
      for (const items of groups(latest).values()) {
        const level = requiredLevel(items);
        const item = items.find(line => needsUpdate(line, level));
        if (item) { pending = { item, level }; break; }
      }
      if (!pending) { latest._tcdKitRepaired = changed; return latest; }
      const response = await fetch(route('change'), {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: pending.item.key, quantity: pending.item.quantity, properties: propertiesFor(pending.item, pending.level) }),
      });
      if (!response.ok) throw new Error('Your basket changed, but its kit saving could not be refreshed. Please refresh the page before checking out.');
      latest = await response.json();
      changed = true;
    }
    throw new Error('Please refresh your basket to finish updating the kit saving.');
  }

  function repair(seed) {
    const task = queue.catch(() => {}).then(() => reconcile(seed));
    queue = task;
    return task;
  }

  function appliedLineSaving(item) {
    const original = Number(item.original_line_price);
    const final = Number(item.final_line_price);
    const priceSaving = item.final_line_price != null && Number.isFinite(original) && Number.isFinite(final)
      ? Math.max(0, original - final) : 0;
    const allocations = (item.line_level_discount_allocations || []).reduce((total, allocation) => total + (Number(allocation.amount) || 0), 0);
    return Math.max(0, Number(item.line_level_total_discount) || 0, allocations, priceSaving);
  }

  function savingsConfirmed(cart) {
    return [...groups(cart).values()].every(items => {
      const level = requiredLevel(items);
      if (!level) return true;
      const expected = items.reduce((total, item) => total + Math.floor(item.original_line_price * level / 100), 0);
      const applied = items.reduce((total, item) => total + appliedLineSaving(item), 0);
      return expected > 0 && applied >= expected;
    });
  }

  window.tcdKitIntegrity = { repair, requiredLevel, propertiesFor, savingsConfirmed, appliedLineSaving };

  async function repairVisibleCart() {
    if (!document.querySelector('[data-tcd-kit-id]')) return;
    try {
      const cart = await repair();
      if (cart._tcdKitRepaired && window.tcd?.cart) await window.tcd.cart.refresh();
    } catch (error) {
      console.warn(error.message);
    }
  }

  document.addEventListener('tcd:cart:refreshed', repairVisibleCart);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repairVisibleCart, { once: true });
  else repairVisibleCart();

  async function checkout(event, control) {
    if (!document.querySelector('[data-tcd-kit-id]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    control?.setAttribute('aria-busy', 'true');
    try {
      const cart = await repair();
      if (!savingsConfirmed(cart)) throw new Error('Your kit saving has not been applied. Please contact our team before paying, so we can check the price for you.');
      window.location.assign(`${root()}checkout`);
    } catch (error) {
      window.alert(error.message);
      checkoutBusy = false;
      control?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', event => {
    const control = event.target.closest('[name="checkout"], a[href="/checkout"], a[href$="/checkout"]');
    if (control) checkout(event, control);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') checkout(event, event.submitter);
  }, true);
})();

